import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react'
import { Redirect } from 'react-router-dom'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import { userTypes } from '@messenger/consts'
import { Navigation } from 'components'
import { Button } from '@/components/priority'
import colors from 'utils/styling/colors'
import typeArea from 'styles/typeArea'
import { getAdminFuneralHomes } from '@/api/admin'
import {
  getLotusFuneralHomeLimit,
  updateLotusFuneralHomeLimit,
  type LotusFuneralHomeLimit,
  type LotusFuneralHomeLimitChange,
} from '@/api/lotusFuneralHomeLimit'
import { userAtom } from '@/atoms/_globalAtoms'

const TID = 'lotus-funeral-home-limit'

type FuneralHome = { id: string; name: string }

/**
 * The admin page where a super admin sets where Lotus text boxes are offered:
 * the limit switch, and the funeral homes the limit lists. While the limit is
 * on, the editor offers its Lotus controls only for an order of a listed
 * funeral home; while it is off, everywhere. Each environment keeps its own
 * setting. The page is internal, so its copy is plain English. Anyone but a
 * super admin, whom the routes it calls also require, is sent to the
 * dashboard.
 */
export const EditorTextBoxFuneralHomeLimitPage = () => {
  const user = useRecoilValue(userAtom)
  const isSuperAdmin = user?.type === userTypes.superAdmin

  const [limit, setLimit] = useState<LotusFuneralHomeLimit | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<FuneralHome[] | null>(null)
  const searchTokenRef = useRef(0)

  useEffect(() => {
    if (!isSuperAdmin) return
    let current = true
    getLotusFuneralHomeLimit()
      .then(({ data }) => {
        if (current) setLimit(data)
      })
      .catch(() => {
        if (current) setError('The Lotus limit could not be loaded.')
      })
    return () => {
      current = false
    }
  }, [isSuperAdmin])

  const change = useCallback(async (body: LotusFuneralHomeLimitChange) => {
    setBusy(true)
    setError(null)
    try {
      const { data } = await updateLotusFuneralHomeLimit(body)
      setLimit(data)
    } catch {
      setError('The change was not saved. Try again.')
    } finally {
      setBusy(false)
    }
  }, [])

  const search = useCallback(
    async (event: FormEvent) => {
      event.preventDefault()
      const token = ++searchTokenRef.current
      try {
        const { data } = await getAdminFuneralHomes(query.trim())
        if (token === searchTokenRef.current) setResults(data)
      } catch {
        if (token === searchTokenRef.current)
          setError('Funeral homes could not be searched.')
      }
    },
    [query],
  )

  // The signed-in user arrives after the first render on a direct load, so
  // no user yet means still loading, not "not a super admin".
  if (user === null) return null
  if (!isSuperAdmin) return <Redirect to="/dashboard" />

  const listedIds = limit?.funeralHomes.map((home) => home.id) ?? []
  const unlisted = (results ?? []).filter(
    (home) => !listedIds.includes(home.id),
  )

  return (
    <>
      <Navigation type="admin" />
      <Page data-testid={`${TID}-page`}>
        <Title>Lotus text boxes</Title>
        <Subtitle>
          While the limit is on, the editor offers Lotus text boxes (the Use new
          text boxes switch, the convert icon and turning a page set over) only
          on orders of the funeral homes listed here. While it is off, they are
          offered on every order. This setting belongs to this environment
          alone.
        </Subtitle>
        {error !== null && (
          <ErrorText role="alert" data-testid={`${TID}-error`}>
            {error}
          </ErrorText>
        )}
        {limit === null ? (
          error === null && <p data-testid={`${TID}-loading`}>Loading…</p>
        ) : (
          <>
            <Row>
              <span
                data-testid={`${TID}-state`}
                data-enabled={limit.enabled ? 'true' : 'false'}
              >
                {limit.enabled
                  ? 'Limit on: only the funeral homes below'
                  : 'Limit off: every funeral home'}
              </span>
              <Button
                type="button"
                disabled={busy}
                aria-pressed={limit.enabled}
                onClick={() => void change({ enabled: !limit.enabled })}
                data-testid={`${TID}-toggle`}
              >
                {limit.enabled ? 'Turn the limit off' : 'Turn the limit on'}
              </Button>
            </Row>
            <SectionTitle>Listed funeral homes</SectionTitle>
            {limit.funeralHomes.length === 0 ? (
              <p data-testid={`${TID}-empty`}>No funeral home is listed.</p>
            ) : (
              <List data-testid={`${TID}-list`}>
                {limit.funeralHomes.map((home) => (
                  <ListItem key={home.id} data-testid={`${TID}-row-${home.id}`}>
                    <span>{home.name}</span>
                    <Button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        void change({
                          funeralHomeIds: listedIds.filter(
                            (id) => id !== home.id,
                          ),
                        })
                      }
                      data-testid={`${TID}-remove-${home.id}`}
                    >
                      Remove
                    </Button>
                  </ListItem>
                ))}
              </List>
            )}
            <SectionTitle>Add a funeral home</SectionTitle>
            <SearchForm onSubmit={search}>
              <SearchInput
                type="search"
                value={query}
                placeholder="Funeral home name"
                aria-label="Funeral home name"
                onChange={(event) => setQuery(event.target.value)}
                data-testid={`${TID}-search-input`}
              />
              <Button type="submit" data-testid={`${TID}-search-button`}>
                Search
              </Button>
            </SearchForm>
            {results !== null && unlisted.length === 0 && (
              <p data-testid={`${TID}-no-results`}>
                No unlisted funeral home matches.
              </p>
            )}
            <List>
              {unlisted.map((home) => (
                <ListItem
                  key={home.id}
                  data-testid={`${TID}-result-${home.id}`}
                >
                  <span>{home.name}</span>
                  <Button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void change({ funeralHomeIds: [...listedIds, home.id] })
                    }
                    data-testid={`${TID}-add-${home.id}`}
                  >
                    Add
                  </Button>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </Page>
    </>
  )
}

/**
 * The page's own text (titles, the limit line, funeral home names, the
 * loading and empty notes) takes the theme's text colour from here: the app's
 * global styles set a themed background but no text colour, so without it the
 * text keeps the browser's black and disappears on the dark theme.
 */
const Page = styled.div`
  padding: 32px;
  max-width: 720px;
  color: ${colors('gray1')};
`

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

const Subtitle = styled.p`
  margin: 0 0 24px 0;
  color: ${colors('gray2')};
`

const SectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  margin: 24px 0 8px 0;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`

const ListItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 8px 0;
  border-bottom: 1px solid ${colors('gray4')};
`

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

/** The search field, in the app's shared field style (as the priority Input). */
const SearchInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  height: 42px;

  ${typeArea}
`

const ErrorText = styled.p`
  color: ${colors('error')};
`
